import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { ActivityIndicator, FlatList, Image, Text, TouchableOpacity, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { Team } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PatternBackground } from '@/src/presentation/components';
import { createStyles } from './MyTeamsScreen.styles';

interface MyTeamsScreenProps {
  teams: Team[];
  currentUserId: string;
  loading: boolean;
  onTeamPress: (teamId: string) => void;
  onCreateTeam: () => void;
  colors: ThemeColors;
}

export const MyTeamsScreen: React.FC<MyTeamsScreenProps> = ({
  teams,
  currentUserId,
  loading,
  onTeamPress,
  onCreateTeam,
  colors,
}) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);

  const renderItem = ({ item }: { item: Team }) => {
    const isCaptain = item.userId === currentUserId;
    return (
      <TouchableOpacity
        style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
        onPress={() => onTeamPress(item.id)}
        activeOpacity={0.7}
      >
        <View style={[styles.logoCircle, { borderColor: colors.border, backgroundColor: colors.surface }]}>
          {item.logoUrl ? (
            <Image source={{ uri: item.logoUrl }} style={styles.logoImage} />
          ) : (
            <Ionicons name="people" size={24} color={colors.textTertiary} />
          )}
        </View>
        <View style={styles.cardInfo}>
          <Text style={[styles.teamName, { color: colors.text }]} numberOfLines={1}>{item.name}</Text>
          {item.description ? (
            <Text style={[styles.teamMeta, { color: colors.textSecondary }]} numberOfLines={1}>{item.description}</Text>
          ) : null}
          {isCaptain && (
            <View style={[styles.captainBadge, { backgroundColor: colors.primary + '20' }]}>
              <Text style={[styles.captainBadgeText, { color: colors.primary }]}>{t('teams.captain')}</Text>
            </View>
          )}
        </View>
        <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} style={styles.chevron} />
      </TouchableOpacity>
    );
  };

  return (
    <PatternBackground>
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.createButton} onPress={onCreateTeam}>
            <Ionicons name="add-circle-outline" size={22} color={colors.primaryText} />
            <Text style={styles.createButtonText}>{t('teams.createTeam')}</Text>
          </TouchableOpacity>
        </View>

        {loading ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <FlatList
            data={teams}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            contentContainerStyle={teams.length === 0 ? { flex: 1 } : styles.list}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Ionicons name="people-outline" size={64} color={colors.textTertiary} />
                <Text style={[styles.emptyTitle, { color: colors.text }]}>{t('teams.noTeams')}</Text>
                <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>{t('teams.noTeamsDesc')}</Text>
              </View>
            }
          />
        )}
      </View>
    </PatternBackground>
  );
};
